import { useMemo, useState } from 'react'

import { Tooltip } from 'antd'
import { connect } from 'react-redux'
import { isFunction } from 'lodash'
import { faCommentDots, faFile } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { OverlaySettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import {
  getInternshipNotes,
  getOwnership,
  getSchoolAbbreviation,
  getSchoolName,
  getSchoolYearName,
  getSectionNameOrFull,
  getState,
  getStudentFullname,
  isContextDraggable,
  Ownership
} from '../../../../utils/entities/internship'
import { dateGetters } from '../../../../utils/entities/common'
import { InternshipStates, InternshipStatesString } from '../../../../utils/constants'
import { selectUser } from '../../../../reducers/UserReducer'
import { isInstitutionUser } from '../../../../utils/roles'
import { getStatesPellet } from '../../../../Components/shared/InternshipsManager/utils/componentGetters'
import useInternship from '../../../../utils/api/hooks/useInternship'
import InternshipRejection from '../../../../Components/shared/InternshipsManager/InternshipRejection'
import { TYPES as InternshipDocumentTypes } from '../../../../utils/entities/internshipDocument'
import { ColorsPalette } from '../../../../config/colors'
import TooltipIcon from '../../../../Components/icons/TooltipIcon'

const { PRE_ACCEPTED, ACCEPTED, CREATED, PENDING, REFUSED } = InternshipStates

/** Quoted block echoing the orange corner marker, so the note stands out among the tooltip's plain lines. */
const NOTE_STYLE = {
  borderLeft: `3px solid ${ColorsPalette.ORANGE}`,
  margin: '6px 0px',
  padding: '2px 0px 2px 6px'
}

const SPAN_STYLE = {
  justifyContent: 'center', whiteSpace: 'nowrap', width: '100%', textAlign: 'center', overflowY: 'hidden', overflowX: 'hidden', textOverflow: 'ellipsis', minWidth: '1em'
}

const OWNERSHIP_LABELS = {
  [Ownership.INSTITUTION]: ['internship_occupancy_displayer.ownership.institution.self', 'internship_occupancy_displayer.ownership.institution.other'],
  [Ownership.SCHOOL]: ['internship_occupancy_displayer.ownership.school.other', 'internship_occupancy_displayer.ownership.school.self']
}

const mapStateToProps = state => ({ user: selectUser(state), t: selectTranslate(state) })

const InternshipOccupancyDisplayer = ({ internship, overlay = new OverlaySettings(), isSectionException = false, user, t, onChange }) => {
  const internshipHandler = useInternship({ callback: onChange })

  const [internshipToRefuse, setInternshipToRefuse] = useState(null)

  const convention = useMemo(() => {
    const documents = Array.isArray(internship?.documents) ? internship.documents : []

    return documents.find(document => document.type === InternshipDocumentTypes.CONVENTION) ?? null
  }, [internship])

  const notes = useMemo(() => getInternshipNotes(internship), [internship])

  const labels = useMemo(() => {
    const rawLabels = {
      student: getStudentFullname(internship),
      school: getSchoolAbbreviation(internship),
      schoolName: getSchoolName(internship),
      section: getSectionNameOrFull(internship),
      year: getSchoolYearName(internship),
      state: InternshipStatesString[getState(internship)],
      ownership: Ownership[getOwnership(internship)]
    }

    const labels = {}

    Object.keys(rawLabels).forEach(key => {
      labels[key] = rawLabels[key] !== '/' ? rawLabels[key] : null
    })

    labels.ownership = OWNERSHIP_LABELS[rawLabels.ownership][isInstitutionUser(user) ? 0 : 1]

    return labels
  }, [internship, user])

  const datesFromTo = useMemo(() => [
    t('common.from.capitalised'),
    dateGetters.startDate(internship),
    t('common.to'),
    dateGetters.endDate(internship)
  ].map((element, i) => <span key={i} className='mr-4'>{element}</span>), [internship, t])

  const canEdit = useMemo(() => isContextDraggable(user, internship), [user, internship])

  const tooltipTitle = useMemo(() => (
    <div>
      <strong>{labels.student ? labels.student : t('common.no_data.student')}</strong> <br />
      {notes.map(note => (
        <div key={note.id} style={NOTE_STYLE}>
          <FontAwesomeIcon className='mr-4' color={ColorsPalette.ORANGE} icon={faCommentDots} />
          {note.note}
        </div>
      ))}
      {labels.state && <><span>{t(labels.state)}</span><br /></>}
      {datesFromTo} <br />  <br />
      {labels.school ? labels.school : t('common.no_data.school')} <br />
      {labels.section ? labels.section : t('common.no_data.section')} <br />
      {labels.year ? labels.year : t('common.no_data.school_year')}  <br />
      {isSectionException && <><br /><span><em>{t('internship_occupancy_displayer.is_section_exception')}</em></span><br /></>}
      {labels.ownership && <><br /><span>{t(labels.ownership)}</span><br /></>}
      {' ('}{canEdit ? t('common.can_edit') : t('common.cannot_edit')}{')'}
    </div>
  ), [labels, datesFromTo, notes, t])

  const StatesPellet = getStatesPellet(user.context)

  const handleStateChange = state => {
    const calls = {
      [PENDING]: internshipHandler.setAsPending,
      [ACCEPTED]: internshipHandler.accept,
      [PRE_ACCEPTED]: internshipHandler.preAccept,
      [CREATED]: internshipHandler.setAsCreated,
      [REFUSED]: setInternshipToRefuse
    }

    if (isFunction(calls[state])) {
      calls[state](internship)
    }
  }

  return (
    <>
      <Tooltip title={tooltipTitle} className='w-100 h-100'>
        <div className='school-quota-displayer flex-row w-100 h-100'>
          {notes.length > 0 && <div className='internship-note-corner' />}
          <div className='flex-row w-100 h-100'>
            <span style={SPAN_STYLE}>
              {overlay.displayContent && (
                <>
                  <span className='mr-4' style={SPAN_STYLE}><b>{labels.student ? labels.student : ''}</b></span>
                  <span className='mr-8' style={SPAN_STYLE}>{labels.school ? `${labels.student ? ' — ' : ''}${labels.school}` : ''}{labels.section ? `${labels.school ? ' — ' : ''}${labels.section}` : ''}{labels.year ? ` (${labels.year})` : ''}</span>
                </>
              )}
            </span>
            {overlay.displayConvention === true && convention && (
              <div className='align-self-center mr-2'>
                <TooltipIcon
                  color={ColorsPalette.BLUE_ICON}
                  icon={faFile}
                  title={t('internship_request_file.types.convention')}
                />
              </div>
            )}

            {overlay.displayStatuses === true && (
              <div className='align-self-center mr-4'>
                <StatesPellet internship={internship} isDropdown onChange={handleStateChange} />
              </div>
            )}
          </div>
        </div>
      </Tooltip>
      <InternshipRejection
        internship={internshipToRefuse}
        onClose={() => setInternshipToRefuse(null)}
        onRejection={onChange}
      />
    </>
  )
}

export default connect(mapStateToProps)(InternshipOccupancyDisplayer)
